import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UpdateAuthUserRequest } from "@/lib/validation/authUsers";
import { fetchGeneralApiGet, fetchGeneralApiPut } from "../utils";
import type { AuthUserPermissionsDetail } from "./types";

export type {
  AuthPermissionAction,
  AuthPermissionResource,
  AuthPermissionScope,
  AuthPermissionScopeDimension,
  AuthPermissionState,
  AuthPermissionVariant,
  AuthUserMembership,
  AuthUserOrganizationType,
  AuthUserPermission,
  AuthUserPermissionsDetail,
  AuthUserRelation,
  AuthUserRelationType,
} from "./types";

export const authUsersQueryKey = ["general_api", "auth", "permissions", "users"] as const;

function authUserQueryKey(authUserId: string) {
  return [...authUsersQueryKey, authUserId] as const;
}

/** A failed general API response, kept whole so callers can read its `data.reason`. */
export class AuthUserRequestError extends Error {
  readonly data: unknown;

  constructor(message: string, data: unknown) {
    super(message);
    this.name = "AuthUserRequestError";
    this.data = data;
  }
}

function authUserPath(authUserId: string): string {
  return `auth/permissions/betterAuth/users/${encodeURIComponent(authUserId)}`;
}

export function getAuthUserPermissions(authUserId: string) {
  return fetchGeneralApiGet<AuthUserPermissionsDetail>(authUserPath(authUserId));
}

/** A client or non-client login's memberships, permissions and customer links. */
export function useGetAuthUserPermissions(authUserId: string) {
  return useQuery({
    queryKey: authUserQueryKey(authUserId),
    queryFn: async (): Promise<AuthUserPermissionsDetail> => {
      const response = await getAuthUserPermissions(authUserId);
      if (!response.success) {
        throw new AuthUserRequestError(response.message || "Unable to load the mobile login", response.data);
      }
      return response.data;
    },
    // 400/403/404 won't change on retry.
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** Replaces one membership's permissions and the login's complete set of customer links in one request. */
export function updateAuthUser(authUserId: string, request: UpdateAuthUserRequest) {
  return fetchGeneralApiPut<AuthUserPermissionsDetail>(authUserPath(authUserId), request);
}

export function useUpdateAuthUser(authUserId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateAuthUserRequest) => updateAuthUser(authUserId, request),
    onSuccess: (result) => {
      // The response is the saved detail, so it replaces the cached one without another read.
      if (result.success) {
        queryClient.setQueryData(authUserQueryKey(authUserId), result.data);
      }
    },
  });
}
